import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";

import { cn } from "@/lib/utils";

const flowTileVariants = cva(
  "inline-flex shrink-0 items-center justify-center rounded-xl border border-border/60 bg-background shadow-black/5 shadow-sm [&>svg]:shrink-0",
  {
    variants: {
      size: {
        xs: "size-6 [&>svg]:size-3",
        sm: "size-8 [&>svg]:size-4",
        md: "size-10 [&>svg]:size-5",
        lg: "size-14 [&>svg]:size-7",
        xl: "size-18 [&>svg]:size-9",
      },
      variant: {
        default: "",
        muted: "text-muted-foreground",
      },
    },
    defaultVariants: {
      size: "md",
      variant: "default",
    },
  }
);

type FlowTileProps = {
  children: ReactNode;
  /**
   * Accessible label for the tile. Sets both `aria-label` and `title`
   * so screen readers announce it and sighted users see a hover tooltip.
   */
  label?: string;
} & Omit<ComponentProps<"span">, "title"> &
  VariantProps<typeof flowTileVariants>;

function FlowTile({
  children,
  className,
  label,
  size,
  variant,
  ...props
}: FlowTileProps) {
  const a11yProps = label
    ? ({ "aria-label": label, role: "img", title: label } as const)
    : ({ "aria-hidden": true } as const);

  return (
    <span
      className={cn(flowTileVariants({ size, variant, className }))}
      data-slot="flow-tile"
      {...a11yProps}
      {...props}
    >
      {children}
    </span>
  );
}

export { FlowTile, flowTileVariants };
export type { FlowTileProps };
